// client/src/utils/itemName.ts
//
// Derives a clean product name from a vendor-invoice line description.
//
// Invoice descriptions bundle the product together with everything else printed
// in that column — pack configuration, unit size, seafood size grade, expiry
// dates. Ops reviewing the grid only want the product:
//
//   "SHRIMP 16-20 EZ PEELED IQF 6X5 CASE"  ->  "SHRIMP EZ PEELED IQF"
//   "TUNA CHUNKS 24x2x10g EXP 12/2026"     ->  "TUNA CHUNKS"
//   "COCA COLA 12x1L"                      ->  "COCA COLA"
//
// The stripped parts are NOT lost: they already live in their own columns
// (splitUow / splitQuantity / splitUnitSize / splitItemUow), and `description`
// itself is kept on the line as the audit trail and as the inventory matcher's
// input. This only changes what the grid shows.
//
// One stripped part had nowhere to go, which is why `splitItemName` exists:
//
//   "160 Whole Chicken (7 bxs)"  ->  { name: "160 Whole Chicken", packingNote: "7 bxs" }
//
// See the PACKING_COUNT_RE comment below for why that count is a fact about
// THIS delivery and cannot be derived from anything else on the line.
//
// MIRRORED FILE: src/utils/itemName.js is a line-for-line twin. CRA's
// ModuleScopePlugin forbids importing from src/, so the copy is the only option.
// Change one, change both.

import { repairUnitOcr } from "./unitVocabulary";

// Unit spellings allowed to terminate a size token. Deliberately a fixed list
// rather than [a-zA-Z]+: a greedy pattern would eat the "UP" out of "7UP" and
// the "IN" out of "6IN", turning product names into fragments.
const SIZE_UNIT =
  "OZ|OZS|LBS|LB|KGS|KG|GMS|GM|GRAMS|GRAM|G|MLS|ML|LTRS|LTR|LITRES|LITRE|L|GAL|GALS|CT|CTS";

// How many physical boxes/cases the line arrived in, printed in brackets beside
// the product: "160 Bals Chicken Brewer (2 bxs)", "160 Whole Chicken (7 bxs)"
// (Lawrence Marketing 27036 on QA). Meat is BILLED by weight and PICKED by box,
// and the boxes weigh different amounts, so this count cannot be derived from
// anything — not from the quantity, not from a pack size. It is a fact about
// this delivery that only the supplier's own paper states.
//
// Glued into the item name it forked a Master Catalogue row per box count for
// one product, which is what fragments the catalogue search. `splitItemName`
// takes it OUT of the name and hands it back so it can be kept as the line's
// packing note instead.
//
// Deliberately narrow. Both brackets are required and a box word must follow
// the number, so a product legitimately named with a parenthesised figure
// survives: "FROZEN CHIPS/FRIES (2KG)" is a real QA row and KG is not a box.
// Only the box/case/carton family, not PK/PACK — a pack is a pack CONFIG, which
// the patterns below already own.
const PACKING_COUNT_RE = /\(\s*(\d{1,4}\s*(?:BXS?|BOX(?:ES)?|CS|CASES?|CTNS?|CARTONS?))\s*\.?\s*\)/gi;

// Order matters. Each pattern assumes the ones above it have already run:
// grades before sizes (so "16-20OZ" goes whole, not down to a dangling "16-"),
// packs before sizes (so "20x8oz" goes whole rather than leaving "20x").
const STRIP_PATTERNS: RegExp[] = [
  // FIRST, and it is the one pattern whose position is not about what it might
  // eat. It is anchored on both brackets, so nothing above could protect it and
  // nothing below can reach inside it — but its text is CAPTURED and kept as the
  // packing note, and a capture is only worth trusting if it is the supplier's
  // own characters rather than whatever an earlier pattern left behind.
  PACKING_COUNT_RE,
  // Expiry / production markers, with or without the date that follows them.
  /\b(?:EXP|EXPIRY|EXPIRES|EXPIRED|BB|BBD|BBE|MFG|MFD|PROD|PACKED)\b\.?:?\s*[\d/.-]*/gi,
  // Bare dates: 12/2026, 01-06-2027, 3.11.26
  /\b\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}\b/g,
  /\b\d{1,2}[/.-]\d{4}\b/g,
  // Seafood/produce size grades: 16-20, 26-30, 10-12oz
  new RegExp(`\\b\\d{1,3}\\s*-\\s*\\d{1,3}\\s*(?:${SIZE_UNIT})?\\b`, "gi"),
  // Three-factor pack configs: 24x2x10g
  new RegExp(
    `\\b\\d+(?:\\.\\d+)?\\s*[x*]\\s*\\d+(?:\\.\\d+)?\\s*[x*]\\s*\\d+(?:\\.\\d+)?\\s*(?:${SIZE_UNIT})?\\b`,
    "gi"
  ),
  // Two-factor pack configs: 6X5, 12x1L, 18x330ml
  new RegExp(`\\b\\d+(?:\\.\\d+)?\\s*[x*]\\s*\\d+(?:\\.\\d+)?\\s*(?:${SIZE_UNIT})?\\b`, "gi"),
  // Plain unit sizes: 14OZ, 330 ML, 1.5KG
  new RegExp(`\\b\\d+(?:\\.\\d+)?\\s*(?:${SIZE_UNIT})\\b`, "gi"),
  // Vendor part numbers: "PICNIC HAM 7482910" -> "PICNIC HAM". Also catches the
  // YYYYMMDD expiry the Acado layout prints ("... 20270618 18/06/27"), which the
  // date patterns above deliberately don't match.
  //
  // Runs LAST, so every size, pack config and date has already gone: a bare run
  // of digits that survives this far is not measuring anything.
  //
  // Five digits, not four, so a year survives. \b keeps it off alphanumeric
  // codes — "D1091" must not become "D". The lookaround keeps it off the tail of
  // a decimal: without it "1.23456" loses everything after the point.
  /\b(?<![.,])\d{5,}\b(?![.,])/g,
];

// Packaging words are only stripped when they TRAIL the name. "CASE" at the end
// of "COD LOINS CASE" is packaging; in "CASE OF COKE" it is the product. The
// phrase goes whole: "per case", "CASE of", "Case of 10", "CASE of x 12" (D185.3).
const TRAILING_PACK_RE =
  /[\s,\-/]*\b(?:PER\s+)?(?:CASE|CASES|CS|CSE|CTN|CTNS|CARTON|CARTONS|BOX|BOXES|BAG|BAGS|PACK|PACKS|PKT|PKTS|PK|TIN|TINS|CAN|CANS|PCS|PC|EA|EACH|DOZ|DOZEN|BTL|BOTTLE|BOTTLES|JAR|JARS|TRAY|TRAYS|TUB|TUBS|PAIL|PAILS|SACK|SACKS|DRUM|DRUMS|ROLL|ROLLS)\b(?:\s+OF\b(?:\s*[x*]?\s*\d{1,4})?|\s*[x*]\s*\d{1,4})?\s*$/i;

/**
 * Clear up what a removal left behind: an emptied bracket, doubled separators,
 * then any separator or stray punctuation clinging to either end.
 *
 * Shared by both names below so they cannot tidy differently — the whole point
 * of `nameWithoutPackingCount` is that it differs from `name` by the packing
 * count and by NOTHING else.
 */
function tidy(text: string): string {
  return (
    String(text)
      // Brackets emptied by a removal: "FROZEN CHIPS/FRIES (2KG)" left a literal
      // "( )" in the name, which reached a client-facing price list. Only a
      // bracket pair with nothing between it goes — "WINE (RED)" is a name, and
      // a rule that trimmed a trailing ")" would make it "WINE (RED".
      .replace(/\(\s*[,\-/@&+.*]*\s*\)/g, " ")
      .replace(/\s+/g, " ")
      .replace(/\s*([,\-/@&+])\s*\1+/g, " ")
      // Note the char class has no letters in it: an earlier version included
      // "x" for a dangling multiplication sign and quietly turned "XTRA LARGE"
      // into "TRA LARGE".
      .replace(/^[\s,\-/@&+.*]+/, "")
      .replace(/[\s,\-/@&+.*]+$/, "")
      // An opening bracket with nothing left after it — "TUNA (24x2x10g" without
      // its closing bracket. Trailing only: a name cannot begin mid-bracket.
      .replace(/\s*\(\s*$/, "")
      .trim()
  );
}

/**
 * The product name, and the box/case count that was printed beside it.
 *
 * `packingNote` is "" on every description that does not print one — which is
 * most of them. It is free text on purpose: the count arrives spelled the way
 * the supplier spells it ("2 bxs", "1 box", "9 cases") and nothing does
 * arithmetic on it, because on catch-weight goods the boxes weigh different
 * amounts and no number of boxes can be worked out from a weight.
 *
 * `name` falls back to the original description whenever stripping would leave
 * nothing usable — a blank name in the grid is worse than a noisy one, and some
 * vendors genuinely print a description that is nothing but a size.
 *
 * `nameWithoutPackingCount` is the description with the packing count removed
 * AND NOTHING ELSE — no size strip, no pack config, no date, no trailing
 * packaging word, and not even the OCR unit repair, which would quietly turn
 * "140Z" into "14OZ" in a name nobody asked to change. It exists for the
 * scoped backfill (`scripts/backfill-catalogue-item-names.js
 * --packing-count-only`), where the owner has approved removing the box count
 * from 29 catalogue rows and explicitly NOT the 576 other renames the full
 * cleaner would make in the same pass. Deriving it here rather than in the
 * script is the point: the pattern has one owner, and a second copy of it in a
 * migration is how a migration and the live code start disagreeing about what
 * a box count is.
 */
export function splitItemName(description: unknown): {
  name: string;
  packingNote: string;
  nameWithoutPackingCount: string;
} {
  const original = String(description ?? "")
    .replace(/\s+/g, " ")
    .trim();
  if (!original) return { name: "", packingNote: "", nameWithoutPackingCount: "" };

  // Repair first: an unrepaired "140Z" is not recognisable as a unit size, so it
  // would survive stripping and end up sitting in the middle of the name.
  let out = repairUnitOcr(original).text;

  // Read the count off the repaired text before anything is removed from it.
  // `lastIndex` is reset because the pattern is /g and shared with the strip
  // loop below — a /g regex carries its cursor between calls, and one left
  // mid-string makes the very next line on the invoice miss its own count.
  const packing: string[] = [];
  PACKING_COUNT_RE.lastIndex = 0;
  for (let m = PACKING_COUNT_RE.exec(out); m; m = PACKING_COUNT_RE.exec(out)) {
    packing.push(m[1].replace(/\s+/g, " ").trim());
  }
  PACKING_COUNT_RE.lastIndex = 0;

  // The count taken off the ORIGINAL text, not the repaired one, and with none
  // of the patterns below applied. Anything else here would be a change the
  // scoped backfill did not ask for.
  const countOnly = packing.length ? tidy(original.replace(PACKING_COUNT_RE, " ")) : original;
  PACKING_COUNT_RE.lastIndex = 0;

  for (const re of STRIP_PATTERNS) {
    re.lastIndex = 0;
    out = out.replace(re, " ");
  }

  out = tidy(out);

  // Run twice: "... 6X5 CASE" leaves "CASE" trailing only after the pack config
  // has gone, and "... CASE PCS" hides a second word behind the first.
  //
  // Deliberately NOT applied to `countOnly`: "COD LOINS CASE" losing its
  // trailing "CASE" is the packaging strip doing its job, and it is not the
  // packing count.
  out = out.replace(TRAILING_PACK_RE, "").replace(TRAILING_PACK_RE, "").trim();

  const packingNote = packing.join(", ");
  // A name with no letters left is not a name — the same fallback `name` takes.
  const usable = (s: string) => s.length >= 2 && /[a-z]/i.test(s);
  const nameWithoutPackingCount = usable(countOnly) ? countOnly : original;

  if (!usable(out)) return { name: original, packingNote, nameWithoutPackingCount };
  return { name: out, packingNote, nameWithoutPackingCount };
}

/**
 * Strips pack/size/grade/date noise out of a line description.
 *
 * The long-standing entry point, and the only one most callers want: everything
 * that reads a NAME goes through here. `splitItemName` above is for the one
 * caller that also needs the box count back.
 */
export function cleanItemName(description: unknown): string {
  return splitItemName(description).name;
}
